"use strict";
/* eslint-disable @typescript-eslint/comma-dangle */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EmployeeCardProvider = exports.useEmployeeCard = void 0;
const const_1 = require("@app/const");
const user_details_1 = require("@app/reducer/user-details");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const error_handler_1 = require("@app/util/error-handler");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const user_details_2 = require("../request/user-details");
const advanced_section_1 = require("../ui/sections/advanced-section");
const contracts_section_1 = require("../ui/sections/contracts-section");
const dashboard_section_1 = require("../ui/sections/dashboard-section");
const data_section_1 = require("../ui/sections/data-section");
const log_section_1 = require("../ui/sections/log-section");
const report_section_1 = require("../ui/sections/report-section");
const settings_section_1 = require("../ui/sections/settings-section");
const EmployeeCardContext = react_1.default.createContext(undefined);
const useEmployeeCard = () => {
    const context = react_1.default.useContext(EmployeeCardContext);
    if (!context) {
        throw new Error('useEmployeeCardV2 must be used within a EmployeeCardV2Provider');
    }
    return context;
};
exports.useEmployeeCard = useEmployeeCard;
const EmployeeCardProvider = ({ children, userId }) => {
    const [selectedTab, setSelectedTab] = react_1.default.useState('dashboard');
    const [_isLoading, setIsLoading] = react_1.default.useState(false);
    const [selectedMonth, setSelectedMonth] = react_1.default.useState((0, moment_1.default)().format('YYYY-MM'));
    const currentPeriod = react_1.default.useMemo(() => ({
        start: (0, moment_1.default)(selectedMonth, 'YYYY-MM').startOf('month').toDate(),
        end: (0, moment_1.default)(selectedMonth, 'YYYY-MM').endOf('month').toDate(),
    }), [selectedMonth]);
    const [footerExtraContentTarget, setFooterExtraContentTarget] = react_1.default.useState(null);
    const [headerExtraContentTarget, setHeaderExtraContentTarget] = react_1.default.useState(null);
    const workspaceId = (0, store_1.useRootSelector)((state) => state.workspaceId);
    const { workspace, me } = (0, store_1.useRootSelector)((state) => ({
        workspace: state.workspaces.find((ws) => ws.id === state.workspaceId),
        me: state.me,
    }));
    const userIsMe = userId === me?.id;
    const amIManager = util_1.permissionsUtil.canRead([const_1.PERMISSIONS.MNG_CALENDAR]) &&
        util_1.permissionsUtil.canRead([const_1.PERMISSIONS.WORKSPACE.EMPLOYEES]);
    const userDetail = (0, store_1.useRootSelector)((state) => state.userDetails);
    const dispatch = (0, store_1.useRootDispatch)();
    react_1.default.useEffect(() => {
        const action = async () => {
            setIsLoading(true);
            const data = await (0, user_details_2.requestUserDetails)({
                workspaceId: workspaceId ?? '',
                userId: userId ?? '',
            });
            if (!(0, error_handler_1.isError)(data)) {
                dispatch((0, user_details_1.setUserDetails)(data?.user));
                dispatch((0, user_details_1.setEmployeeId)(data?.customData?.employeeId));
                dispatch((0, user_details_1.setUserTerminalPIN)(data?.terminalPIN));
            }
            setIsLoading(false);
        };
        void action();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);
    const employeeCardTabs = react_1.default.useMemo(() => [
        {
            label: (0, util_1.t)('DASHBOARD'),
            id: 'dashboard',
            icon: 'template',
            component: react_1.default.createElement(dashboard_section_1.DashboardSection, null),
            isVisible: true,
        },
        {
            label: (0, util_1.t)('EMPLOYEE_PROFILE_CARD_DATA'),
            id: 'data',
            icon: 'person',
            component: react_1.default.createElement(data_section_1.DataSection, null),
            isVisible: true,
        },
        {
            label: util_1.miscUtil.getFilteredContentForOrganization('agreement_name', (0, util_1.t)('CONTRACTS')) ?? '??',
            id: 'contracts',
            icon: 'work',
            component: react_1.default.createElement(contracts_section_1.ContractsSection, null),
            isVisible: true,
        },
        {
            label: (0, util_1.t)('REPORT'),
            id: 'report',
            icon: 'report',
            component: react_1.default.createElement(report_section_1.ReportSection, null),
            isVisible: !(userIsMe && !amIManager),
        },
        {
            label: (0, util_1.t)('LOG'),
            id: 'log',
            icon: 'cycle',
            component: react_1.default.createElement(log_section_1.LogSection, null),
            isVisible: !(userIsMe && !amIManager),
        },
        {
            label: (0, util_1.t)('EMPLOYEE_PROFILE_CARD_OTHER'),
            id: 'settings',
            icon: 'settings',
            component: react_1.default.createElement(settings_section_1.SettingsSection, null),
            isVisible: !(userIsMe && !amIManager),
        },
        {
            label: (0, util_1.t)('ADVANCED'),
            id: 'advanced',
            icon: 'settings',
            component: react_1.default.createElement(advanced_section_1.AdvancedSection, null),
            isVisible: userIsMe,
        },
    ], 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    react_1.default.useEffect(() => {
        return () => {
            dispatch((0, user_details_1.unsetUser)());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const context = {
        selectedTab,
        setSelectedTab,
        selectedMonth,
        setSelectedMonth,
        period: currentPeriod,
        employee: {
            user: userDetail.user,
            employeeId: userDetail.employeeId,
            terminalPIN: userDetail.terminalPIN,
        },
        userId,
        userIsMe,
        employeeCardTabs,
        workspace,
        footerExtraContentTarget,
        setFooterExtraContentTarget,
        headerExtraContentTarget,
        setHeaderExtraContentTarget,
        isLoading: _isLoading,
        setIsLoading,
    };
    return (react_1.default.createElement(EmployeeCardContext.Provider, { value: context }, children?.(context)));
};
exports.EmployeeCardProvider = EmployeeCardProvider;
